import React, { useState, useEffect, useCallback } from "react";
import { Platform } from "../types";
import { postToAllPlatforms } from "../lib/socialPoster";
import { SocialMediaManager } from "./SocialMediaManager";
import { RefreshCw, Trash2, Clock } from "lucide-react";
import { useLocation } from "react-router-dom";
import {
  getPlatformIcon,
  getPlatformDisplayName,
} from "../utils/platformIcons";
import { getPlatformVideoLimits } from "../utils/videoUtils";
import API from "../services/api";
import { useTranslation } from "react-i18next";
import { useModal } from "../context2/ModalContext";
import DiscardWarningModal from "../components/modals/DiscardWarningModal";
import { useAppContext } from "@/context/AppContext";
import { useNavigationGuard } from "@/hooks/useNavigationGuard";
import { useDraftStoreZustand } from "@/store/useDraftStore";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
type TikTokPrivacyLevel = string;

interface TikTokCreatorInfo {
  nickname?: string;
  max_video_post_duration_sec?: number;
  privacy_level_options?: TikTokPrivacyLevel[];
  can_post?: boolean;
  blocked_reason?: string;
}

interface TikTokSettingsState {
  title: string;
  privacyLevel: TikTokPrivacyLevel | "";
  allowComment: boolean;
  allowDuet: boolean;
  allowStitch: boolean;
  isCommercial: boolean;
  isYourBrand: boolean;
  isBrandedContent: boolean;
}

interface PublishProps {
  posts: any[];
  onBack: () => void;
  onReset: () => void;
  userId: string;
  onDiscardAll?: () => void;
}

interface PlatformScheduleEnabled {
  [platform: string]: boolean;
}
interface PlatformScheduleDates {
  [platform: string]: Date | null;
}

export const PublishPosts: React.FC<PublishProps> = ({
  posts,
  userId,
  onBack,
  onReset,
  onDiscardAll,
}) => {
  const { t } = useTranslation();
  const { openModal } = useModal();
  const {
    connectedPlatforms,
    connectingPlatforms,
    checkConnectedPlatforms,
    loadSchedulelHistory,
    handleConnectPlatform,
    handleDisconnectPlatform,
    fetchPostHistory,
    linkedinPages,
    facebookPages,
    youtubeChannels,
    selectedLinkedinPage,
    selectedFacebookPage,
    selectedYoutubeChannel,
    setSelectedLinkedinPage,
    setSelectedFacebookPage,
    fetchAnalytics,
    setSelectedYoutubeChannel,
    fetchUnreadCount,
    loadDraftsHistory,
    state,
  }: any = useAppContext();

  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>(
    posts.map((p) => p.platform)
  );
  const [publishing, setPublishing] = useState(false);
  const [results, setResults] = useState<Record<string, any> | null>(null);
  const [publishProgress, setPublishProgress] = useState<
    Record<string, "pending" | "success" | "error">
  >({});
  const [publishedPlatforms, setPublishedPlatforms] = useState<Platform[]>([]);
  const [youtubePrivacy, setYoutubePrivacy] = useState("public");

  const [platformScheduleEnabled, setPlatformScheduleEnabled] =
    useState<PlatformScheduleEnabled>({});
  const [platformScheduleDates, setPlatformScheduleDates] =
    useState<PlatformScheduleDates>({});

  const [tiktokCreatorInfo, setTiktokCreatorInfo] =
    useState<TikTokCreatorInfo | null>(null);
  const [tiktokSettings, setTiktokSettings] = useState<TikTokSettingsState>({
    title: "",
    privacyLevel: "",
    allowComment: false,
    allowDuet: false,
    allowStitch: false,
    isCommercial: false,
    isYourBrand: false,
    isBrandedContent: false,
  });
  const [tiktokPostingBlockedReason, setTiktokPostingBlockedReason] = useState<
    string | null
  >(null);

  const handleBackClick = useCallback(() => onBack(), [onBack]);
  const location = useLocation();
  const previousLocationRef = React.useRef(location.pathname);

  useEffect(() => {
    if (location.pathname !== previousLocationRef.current) {
      previousLocationRef.current = location.pathname;
      onDiscardAll?.();
    }
  }, [location.pathname, onDiscardAll]);


  const handleDiscardClick = useCallback(() => {
    openModal(DiscardWarningModal, {
      t,
      onConfirmAction: () => {
        onDiscardAll?.();
      },
    });
  }, [openModal, t, onDiscardAll]);

  const isTikTokSelectedAndConnected = useCallback(
    () =>
      selectedPlatforms.includes("tiktok") &&
      connectedPlatforms.includes("tiktok") &&
      posts.some((p) => p.platform === "tiktok"),
    [selectedPlatforms, connectedPlatforms, posts]
  );

  const validateTikTokSettings = useCallback(() => {
    if (!isTikTokSelectedAndConnected()) return true;
    const tikTokPost = posts.find((p) => p.platform === "tiktok");
    if (!tikTokPost) return true;
    if (tiktokPostingBlockedReason) return false;
    if (!tiktokSettings.title.trim()) return false;
    if (!tiktokSettings.privacyLevel) return false;
    if (tiktokSettings.isCommercial) {
      if (!tiktokSettings.isYourBrand && !tiktokSettings.isBrandedContent) {
        return false;
      }
    }
    const privacyIsPrivate =
      tiktokSettings.privacyLevel.toUpperCase() === "SELF_ONLY" ||
      tiktokSettings.privacyLevel.toUpperCase() === "PRIVATE";
    if (tiktokSettings.isBrandedContent && privacyIsPrivate) return false;
    if (
      tikTokPost.tiktokVideoDurationSec &&
      tiktokCreatorInfo?.max_video_post_duration_sec &&
      tikTokPost.tiktokVideoDurationSec >
        tiktokCreatorInfo.max_video_post_duration_sec + 30
    ) {
      return false;
    }
    return true;
  }, [
    isTikTokSelectedAndConnected,
    posts,
    tiktokPostingBlockedReason,
    tiktokSettings,
    tiktokCreatorInfo,
  ]);

  const { drafts, removeDraft } = useDraftStoreZustand();

  const validateScheduleDates = useCallback((): boolean => {
    const platformsToPublish = selectedPlatforms.filter(
      (p) => connectedPlatforms.includes(p) && !publishedPlatforms.includes(p)
    );

    for (const platform of platformsToPublish) {
      if (!platformScheduleEnabled[platform]) continue;

      const date = platformScheduleDates[platform];
      if (!date) {
        alert(
          `Please select a schedule date for ${getPlatformDisplayName(platform)}`
        );
        return false;
      }

      if (date < new Date()) {
        alert(
          `Schedule date for ${getPlatformDisplayName(platform)} cannot be in the past`
        );
        return false;
      }
    }

    return true;
  }, [
    selectedPlatforms,
    connectedPlatforms,
    publishedPlatforms,
    platformScheduleEnabled,
    platformScheduleDates,
  ]);

  const handlePublish = async () => {
    if (!validateScheduleDates()) return;

    const availablePlatforms = selectedPlatforms.filter(
      (p) => connectedPlatforms.includes(p) && !publishedPlatforms.includes(p)
    );
    if (availablePlatforms.length === 0) return;

    const scheduledPlatforms = availablePlatforms.filter(
      (p) => platformScheduleEnabled[p]
    );
    const immediatePlatforms = availablePlatforms.filter(
      (p) => !platformScheduleEnabled[p]
    );

    setPublishing(true);
    setPublishProgress({});

    try {
      const youtubePost = posts.find((p) => p.platform === "youtube");
      const thumbnailUrl = youtubePost?.thumbnailUrl;

      const buildPost = (post: any) => {
        if (post.platform === "tiktok") {
          return {
            ...post,
            postId: post.postId || post.draftId || post.postHistoryId,
            tiktok_title: post.tiktok_title,
            tiktok_description: post.tiktok_description,
          };
        }

        if (post.platform === "youtube") {
          return {
            ...post,
            postId: post.postId || post.draftId || post.postHistoryId,
            youtubeTitle: post.youtubeTitle,
            youtubeDescription: post.youtubeDescription,
            youtubePrivacy,
          };
        }

        return {
          ...post,
          postId: post.postId || post.draftId || post.postHistoryId,
        };
      };

      for (const platform of scheduledPlatforms) {
        const post = posts.find((p) => p.platform === platform);
        if (!post) continue;

        setPublishProgress((prev) => ({ ...prev, [platform]: "pending" }));

        try {
          const draftPost = drafts[platform];
          const scheduleDate = platformScheduleDates[platform]!;
          const payload = {
            post: {
              ...buildPost(post),
              ...(platform === "youtube" && { thumbnailUrl }),
            },
            pageId:
              platform === "facebook"
                ? selectedFacebookPage
                : platform === "linkedin"
                  ? selectedLinkedinPage
                  : undefined,
            postId: draftPost?.postId,
            scheduledFor: new Date(scheduleDate).toISOString(),
          };

          await API.scheduledPost(payload);
          setPublishProgress((prev) => ({ ...prev, [platform]: "success" }));
          setPublishedPlatforms((prev) => [...prev, platform as Platform]);
          removeDraft(platform);
        } catch (err: any) {
          console.error(`Failed to schedule ${platform}:`, err);
          setPublishProgress((prev) => ({ ...prev, [platform]: "error" }));
        }
      }

      if (immediatePlatforms.length > 0) {
        const immediatePosts = posts
          .filter((p) => immediatePlatforms.includes(p.platform))
          .map(buildPost);

        const publishResults = await postToAllPlatforms(
          immediatePosts,
          (platform, status) => {
            setPublishProgress((prev) => ({ ...prev, [platform]: status }));
            if (status === "success") {
              setPublishedPlatforms((prev) => [...prev, platform as Platform]);
              setSelectedPlatforms((prev) =>
                prev.filter((p) => p !== platform)
              );
            }
          },
          {
            linkedinPageId: selectedLinkedinPage || undefined,
            facebookPageId: selectedFacebookPage || undefined,
            youtubeChannelId: selectedYoutubeChannel || undefined,
            thumbnailUrl: thumbnailUrl || undefined,
          }
        );

        setResults(publishResults);

        const newlyPublished = Object.entries(publishResults)
          .filter(([key, r]) => key !== "_summary" && (r as any).success)
          .map(([p]) => p as Platform);

        if (newlyPublished.length > 0) setTimeout(fetchPostHistory, 500);

        const allDone = availablePlatforms.every((p) =>
          [
            ...publishedPlatforms,
            ...newlyPublished,
            ...scheduledPlatforms,
          ].includes(p)
        );

        if (allDone && onReset) setTimeout(onReset, 2000);
      } else if (scheduledPlatforms.length > 0) {
        setTimeout(onReset, 1500);
      }
    } catch (err: any) {
      console.error("Operation failed:", err);
      setResults({
        _summary: {
          total: availablePlatforms.length,
          successful: 0,
          failed: availablePlatforms.length,
          errors: [`Error: ${err.message}`],
          successes: [],
        },
      });
    } finally {
      setPublishing(false);
      fetchUnreadCount();
      checkConnectedPlatforms();
      loadSchedulelHistory();
      loadDraftsHistory(1, false);
      fetchAnalytics(true);
    }
  };

  const getNextFiveMinuteSlot = () => {
    const now = new Date();
    now.setSeconds(0, 0);
    const minutes = now.getMinutes();
    const remainder = minutes % 5;

    if (remainder !== 0) {
      now.setMinutes(minutes + (5 - remainder));
    }

    return now;
  };

  const togglePlatformSchedule = (platform: string, enabled: boolean) => {
    setPlatformScheduleEnabled((prev) => ({ ...prev, [platform]: enabled }));

    if (enabled && !platformScheduleDates[platform]) {
      setPlatformScheduleDates((prev) => ({
        ...prev,
        [platform]: getNextFiveMinuteSlot(),
      }));
    }
  };

  const activePlatforms = selectedPlatforms.filter(
    (p) => connectedPlatforms.includes(p) && !publishedPlatforms.includes(p)
  );
  const scheduledCount = activePlatforms.filter(
    (p) => platformScheduleEnabled[p]
  ).length;
  const immediateCount = activePlatforms.length - scheduledCount;

  const getButtonLabel = () => {
    if (activePlatforms.length === 0) {
      return publishedPlatforms.length > 0
        ? t("all_selected_platforms_published")
        : t("select_platform_publish");
    }
    if (scheduledCount > 0 && immediateCount > 0) {
      return `Publish ${immediateCount} & Schedule ${scheduledCount}`;
    }
    if (scheduledCount > 0) {
      return `Schedule ${scheduledCount} Platform${scheduledCount > 1 ? "s" : ""}`;
    }
    return t("publish_to_platforms");
  };

  return (
    <div className="theme-bg-light max-w-5xl mx-auto">
      <div className="px-3 lg:p-6">
        <h2 className="text-2xl font-bold theme-text-primary mb-2">
          {t("publish_posts")}
        </h2>

        <div className="p-3 bg-white border border-blue-600 rounded-md">
          <p className="text-sm text-blue-800">
            <span className="font-medium">{connectedPlatforms.length}</span> of{" "}
            <span className="font-medium">{posts.length}</span>{" "}
            {t("platforms_connected")}
          </p>
        </div>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div className="mb-4 mt-4 space-y-4">
            {posts.map((post) => {
              const isConnected = connectedPlatforms.includes(post.platform);
              const isConnecting = connectingPlatforms.includes(post.platform);
              const progress = publishProgress[post.platform];
              const isSelected = selectedPlatforms.includes(post.platform);
              const isPublished = publishedPlatforms.includes(post.platform);

              const durationSec = (post as any).tiktokVideoDurationSec;
              const tiktokDurationLimit =
                post.platform === "tiktok"
                  ? tiktokCreatorInfo?.max_video_post_duration_sec
                  : undefined;
              const disableByDuration =
                post.platform === "tiktok" &&
                !!durationSec &&
                !!tiktokDurationLimit &&
                durationSec > tiktokDurationLimit + 30;

              const mediaUrl = post.mediaUrl || post.imageUrl;
              const isVideo =
                mediaUrl &&
                (/\.(mp4|webm|ogg|mov|avi|mkv|flv|wmv|3gp)(\?.*)?$/i.test(
                  mediaUrl
                ) ||
                  (post as any).isVideoContent ||
                  (post as any).mediaType === "video");

              const videoAspectRatio = (post as any).videoAspectRatio;
              const isShorts =
                videoAspectRatio &&
                videoAspectRatio >= 0.5 &&
                videoAspectRatio <= 0.65;

              const videoLimits = isVideo
                ? getPlatformVideoLimits(post.platform, isShorts)
                : null;

              const canSchedule =
                 isSelected && !isPublished && !disableByDuration;
              const isScheduled = !!platformScheduleEnabled[post.platform];
              const scheduleDate = platformScheduleDates[post.platform];

              return (
                <React.Fragment key={post.platform}>
                  <div className="flex flex-col gap-3 py-4 px-2 md:px-4 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors">
                    <div className="flex flex-row gap-2 md:gap-4 justify-between">
                      <div className="flex flex-row items-center gap-2 md:gap-4">
                        <div className="w-8 h-8 md:w-12 md:h-12 rounded-full flex items-center justify-center">
                          {(() => {
                            const Icon = getPlatformIcon(post.platform);
                            return Icon ? (
                              <Icon className="w-8 h-8 md:w-8 md:h-8" />
                            ) : (
                              <span className="text-lg font-bold">
                                {post.platform.substring(0, 2).toUpperCase()}
                              </span>
                            );
                          })()}
                        </div>

                        <div className="mr-5">
                          <h4 className="font-medium text-slate-900">
                            {getPlatformDisplayName(post.platform)}
                          </h4>
                          <p
                            className={`text-xs ${isConnected ? "text-green-600" : "text-red-600"}`}
                          >
                            {isConnected ? t("connected") : t("not_connected")}
                          </p>

                          {/* {videoLimits && (
                            <div className="mt-2 text-xs text-gray-600 space-y-1">
                              <p className="font-medium text-gray-700">
                                {isShorts ? "Shorts" : "Video"} Limits:
                              </p>
                              <p>
                                Aspect Ratio:{" "}
                                <span className="font-semibold">
                                  {videoLimits.aspectRatio}
                                </span>
                              </p>
                              <p>
                                Max Duration:{" "}
                                <span className="font-semibold">
                                  {videoLimits.maxDuration}
                                </span>
                              </p>
                              <p>
                                Max File Size:{" "}
                                <span className="font-semibold">
                                  {videoLimits.maxFileSize}
                                </span>
                              </p>
                              {videoLimits.notes && (
                                <p className="text-yellow-700 italic mt-1">
                                  ⚠️ {videoLimits.notes}
                                </p>
                              )}
                            </div>
                          )} */}

                          {disableByDuration && (
                            <p className="text-xs text-red-600 mt-1">
                              This video exceeds your TikTok account&apos;s
                              allowed duration.
                            </p>
                          )}

                          {progress && (
                            <p
                              className={`text-xs mt-1 ${
                                progress === "pending"
                                  ? "text-yellow-600"
                                  : progress === "success"
                                    ? "text-green-600"
                                    : "text-red-600"
                              }`}
                            >
                              {progress === "pending"
                                ? isScheduled
                                  ? "Scheduling..."
                                  : "Publishing..."
                                : progress === "success"
                                  ? isScheduled
                                    ? "Scheduled ✓"
                                    : "Published ✓"
                                  : "Failed"}
                            </p>
                          )}
                        </div>

                        {canSchedule && (
                          <div className="lg:flex hidden items-center lg:flex-row flex-col gap-2 md:gap-4">
                            <label className="flex items-center gap-2 md:gap-4 cursor-pointer w-[180px]">
                              <input
                                type="checkbox"
                                className="sr-only hidden"
                                checked={isScheduled}
                                onChange={(e) =>
                                  togglePlatformSchedule(
                                    post.platform,
                                    e.target.checked
                                  )
                                }
                              />

                              <div
                                className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                                  isScheduled ? "bg-purple-600" : "bg-gray-300"
                                }`}
                              >
                                <div
                                  className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                    isScheduled
                                      ? "translate-x-5"
                                      : "translate-x-0"
                                  }`}
                                />
                              </div>

                              <span className="text-xs font-medium text-gray-700 select-none">
                                {`Schedule ${getPlatformDisplayName(post.platform)}`}
                              </span>
                            </label>
                            <div className="flex items-center gap-5">
                              {isScheduled && (
                                <DateTimePicker
                                  className="w-[80%]"
                                  value={
                                    scheduleDate ? dayjs(scheduleDate) : null
                                  }
                                  onChange={(newValue: Dayjs | null) => {
                                    setPlatformScheduleDates((prev) => ({
                                      ...prev,
                                      [post.platform]: newValue
                                        ? newValue.toDate()
                                        : null,
                                    }));
                                  }}
                                  minDateTime={dayjs()}
                                  minutesStep={5}
                                  
                                  slotProps={{
                                    textField: {
                                      size: "small",
                                      placeholder: `Pick date & time for ${getPlatformDisplayName(post.platform)}`,
                                      // Here you can add custom styles
                                      InputProps: {
                                        sx: {
                                          fontSize: "0.875rem",
                                          borderRadius: "0.375rem",
                                          "&.Mui-error .MuiOutlinedInput-notchedOutline":
                                            {
                                              borderColor: "#4CAF50", // Change to your desired color
                                            },
                                          "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                            {
                                              borderColor: "#4CAF50", // Change focus border color (green in this example)
                                            },
                                        },
                                      },
                                    },
                                    popper: {
                                      sx: { zIndex: 50 },
                                    },
                                  }}
                                />
                              )}

                              <div className="lg:block hidden w-full">
                                {post.suggestedTime && isScheduled && (
                                  <p className="text-xs text-gray-500 text-left flex items-center gap-2">
                                    <Clock className="w-4 h-4" />
                                    Suggested Time:{" "}
                                    <strong>{post.suggestedTime}</strong>
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-4">
                        {isConnected && (
                          <>
                            <button
                              onClick={() =>
                                handleConnectPlatform(post.platform)
                              }
                              disabled={isConnecting}
                              className="text-gray-500 hover:text-blue-600 disabled:opacity-50 rounded-md hover:bg-gray-100"
                              title="Refresh connection"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() =>
                                handleDisconnectPlatform(post.platform)
                              }
                              disabled={isConnecting}
                              className="text-gray-500 hover:text-red-600 disabled:opacity-50 rounded-md hover:bg-gray-100"
                              title="Disconnect"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}

                        {isConnected && !isPublished && !disableByDuration && (
                          <label
                            className="flex items-center cursor-pointer"
                            htmlFor={`platform-${post.platform}`}
                          >
                            <input
                              id={`platform-${post.platform}`}
                              type="checkbox"
                              className="sr-only hidden"
                              checked={isSelected}
                              onChange={(e) => {
                                setSelectedPlatforms((prev) =>
                                  e.target.checked
                                    ? [...prev, post.platform]
                                    : prev.filter((p) => p !== post.platform)
                                );

                                if (!e.target.checked) {
                                  setPlatformScheduleEnabled((prev) => {
                                    const n = { ...prev };
                                    delete n[post.platform];
                                    return n;
                                  });
                                  setPlatformScheduleDates((prev) => {
                                    const n = { ...prev };
                                    delete n[post.platform];
                                    return n;
                                  });
                                }
                              }}
                            />
                            <div
                              className={`w-[18px] h-[18px] rounded border transition-all duration-200 flex items-center justify-center ${
                                isSelected
                                  ? "bg-blue-600 border-blue-600 text-white"
                                  : "bg-white border-gray-300 hover:border-blue-500"
                              }`}
                            >
                              {isSelected && (
                                <svg
                                  className="w-4 h-4"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </div>
                          </label>
                        )}

                        {isConnected && isPublished && (
                          <div className="flex items-center gap-1 text-xs md:text-sm px-2 py-1 rounded-md bg-purple-200 text-purple-600 font-medium">
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span>{t("published")}</span>
                          </div>
                        )}

                        {!isConnected && (
                          <button
                            onClick={() => handleConnectPlatform(post.platform)}
                            disabled={isConnecting}
                            className="flex gap-2 px-3 py-1 capitalize rounded-md bg-purple-600 text-sm font-medium text-white disabled:opacity-50"
                          >
                            {isConnecting ? (
                              <>
                                <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                                <span>{t("connecting")}...</span>
                              </>
                            ) : (
                              t("connect")
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {canSchedule && (
                      <div className=" md:pl-16 space-y-2 lg:hidden block">
                        <div
                          className="flex items-center gap-2 cursor-pointer w-fit"
                          onClick={() =>
                            togglePlatformSchedule(post.platform, !isScheduled)
                          }
                        >
                          <div
                            className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                              isScheduled ? "bg-purple-600" : "bg-gray-300"
                            }`}
                          >
                            <div
                              className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                isScheduled ? "translate-x-5" : "translate-x-0"
                              }`}
                            />
                          </div>
                          <span className="text-xs font-medium text-gray-700 select-none">
                            {`Schedule ${getPlatformDisplayName(post.platform)}`}
                          </span>
                        </div>

                        {post.suggestedTime && isScheduled && (
                          <p className="text-xs text-gray-500 text-left flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Suggested Time:{" "}
                            <strong>{post.suggestedTime}</strong>
                          </p>
                        )}

                        {isScheduled && (
                          <div className="flex items-start gap-2 mt-1">
                            <div className="flex-1">
                              <DateTimePicker
                                className="w-[80%]"
                                value={
                                  scheduleDate ? dayjs(scheduleDate) : null
                                }
                                onChange={(newValue: Dayjs | null) => {
                                  setPlatformScheduleDates((prev) => ({
                                    ...prev,
                                    [post.platform]: newValue
                                      ? newValue.toDate()
                                      : null,
                                  }));
                                }}
                                minDateTime={dayjs()}
                                minutesStep={5}
                                ampm
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    placeholder: `Pick date & time for ${getPlatformDisplayName(post.platform)}`,
                                    InputProps: {
                                      sx: {
                                        fontSize: "0.875rem",
                                        borderRadius: "0.375rem",
                                        "&.Mui-error .MuiOutlinedInput-notchedOutline":
                                          {
                                            borderColor: "#4CAF50", // Change to your desired color
                                          },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                          {
                                            borderColor: "#4CAF50", // Change focus border color (green in this example)
                                          },
                                      },
                                    },
                                  },
                                  popper: {
                                    sx: { zIndex: 50 },
                                  },
                                }}
                              />
                              {!scheduleDate && (
                                <p className="text-xs text-red-500 mt-1">
                                  Please select a date & time
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </LocalizationProvider>

        <div className="hidden">
          <SocialMediaManager
            userId={userId || ""}
            onCredentialsUpdate={checkConnectedPlatforms}
            handleConnectPlatform={handleConnectPlatform}
            handleDisconnectPlatform={handleDisconnectPlatform}
          />
        </div>

        {connectedPlatforms.includes("facebook") &&
          selectedPlatforms.includes("facebook") && (
            <div className="mb-4 p-3 bg-white border border-blue-600 rounded-md">
              <h4 className="font-medium text-black mb-2">
                {t("facebook_page_selection")}
              </h4>
              <p className="text-blue-700 text-sm mb-3">
                {t("choose_facebook_page_to_post_to")}
              </p>
              {facebookPages.length > 0 ? (
                <select
                  value={selectedFacebookPage}
                  onChange={(e) => {
                    !state?.analyticsLoading &&
                      setSelectedFacebookPage(e.target.value);
                    !state?.analyticsLoading &&
                      localStorage.setItem(
                        "selectedFacebookPage",
                        e.target.value
                      );
                    !state?.analyticsLoading && fetchAnalytics(true);
                  }}
                  className="w-full p-2.5 border border-blue-600 rounded-md focus:ring-1 focus:ring-blue-500"
                >
                  {facebookPages.map((page: any) => (
                    <option key={page.id} value={page.id}>
                      {page.name} ({page.category})
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-yellow-700 text-sm">
                  Loading Facebook pages... If they don&apos;t appear, check
                  your connection.
                </p>
              )}
            </div>
          )}

        {connectedPlatforms.includes("linkedin") &&
          selectedPlatforms.includes("linkedin") &&
          linkedinPages.length > 0 && (
            <div className="mb-6 p-3 bg-white border border-blue-600 rounded-md">
              <h4 className="font-medium text-black mb-2">
                LinkedIn Page Selection
              </h4>
              <p className="text-blue-700 text-sm mb-3">
                Choose your default LinkedIn page for publishing:
              </p>
              <select
                value={selectedLinkedinPage}
                onChange={(e) => {
                  !state?.analyticsLoading &&
                    setSelectedLinkedinPage(e.target.value);
                  !state?.analyticsLoading &&
                    localStorage.setItem(
                      "selectedlinkedinPage",
                      e.target.value
                    );
                  !state?.analyticsLoading && fetchAnalytics(true);
                }}
                className="w-full p-2.5 border border-blue-600 rounded-md focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Personal Account</option>
                {linkedinPages.map((page: any) => (
                  <option key={page.urn} value={page.urn}>
                    {page.name}
                  </option>
                ))}
              </select>
            </div>
          )}

        {connectedPlatforms.includes("youtube") &&
          selectedPlatforms.includes("youtube") &&
          youtubeChannels.length > 0 && (
            <div className="mb-4 p-4 bg-white border border-blue-600 rounded-md">
              <h4 className="font-medium text-black mb-2">
                {t("youtube_channel_selection")}
              </h4>
              <p className="text-blue-700 text-sm mb-3">
                {t("choose_youtube_channel_to_upload_to")}
              </p>
              <select
                value={selectedYoutubeChannel}
                onChange={(e) => {
                  !state?.analyticsLoading &&
                    setSelectedYoutubeChannel(e.target.value);
                  !state?.analyticsLoading &&
                    localStorage.setItem(
                      "selectedYoutubeChannel",
                      e.target.value
                    );
                  !state?.analyticsLoading && fetchAnalytics(true);
                }}
                className="w-full p-2.5 border border-blue-600 rounded-md focus:ring-1 focus:ring-blue-500"
              >
                {youtubeChannels.map((channel: any) => (
                  <option key={channel.id} value={channel.id}>
                    {channel.snippet.title}
                  </option>
                ))}
              </select>
              <h4 className="font-medium text-black my-2">
                {t("Youtube Privacy Status")}
              </h4>
              <select
                value={youtubePrivacy || "public"}
                onChange={(e) => setYoutubePrivacy(e.target.value)}
                className="w-full hover:cursor-pointer p-2.5 border border-blue-600 rounded-md focus:ring-1 focus:ring-blue-500"
              >
                <option value="public">{t("Public")}</option>
                <option value="private">{t("Private")}</option>
                <option value="unlisted">{t("Unlisted")}</option>
              </select>
              <p className="text-gray-500 text-xs mt-2">
                {t("youtube privacy policy note")}
              </p>
            </div>
          )}

        <button
          onClick={handlePublish}
          disabled={publishing || activePlatforms.length === 0}
          className={`w-full rounded-md py-2.5 px-4 border border-[#7650e3] text-white font-semibold transition-colors mb-4 flex items-center justify-center gap-2 ${
            publishing
              ? "bg-purple-400 cursor-wait"
              : activePlatforms.length === 0
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-purple-600 hover:bg-purple-700"
          }`}
        >
          {publishing ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>
                {scheduledCount > 0 && immediateCount > 0
                  ? "Publishing & Scheduling..."
                  : scheduledCount > 0
                    ? "Scheduling..."
                    : t("publish")}
              </span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{getButtonLabel()}</span>
            </>
          )}
        </button>

        <button
          onClick={handleDiscardClick}
          className="w-full rounded-md theme-bg-light px-4 py-2.5 text-center font-semibold text-base border border-[#7650e3] text-[#7650e3] hover:bg-[#d7d7fc] transition-colors"
        >
          {t("discard_post")}
        </button>

        <button
          onClick={handleBackClick}
          className="mt-4 w-full rounded-md theme-bg-light px-4 py-2.5 text-center font-semibold text-base border border-[#7650e3] text-[#7650e3] hover:bg-[#d7d7fc] transition-colors"
        >
          {t("back")}
        </button>

        {activePlatforms.length === 0 && publishedPlatforms.length === 0 && (
          <p className="mt-3 text-sm text-gray-500 font-medium text-center">
            {t("select_platform_warning")}
          </p>
        )}

        {publishedPlatforms.length > 0 && activePlatforms.length === 0 && (
          <p className="mt-3 text-sm text-green-600 text-center font-medium">
            {t("all_selected_platforms_published_successfully")}{" "}
            {connectedPlatforms.filter(
              (p: any) => !publishedPlatforms.includes(p)
            ).length === 0
              ? "Returning to content creation..."
              : `You can select from ${
                  connectedPlatforms.filter(
                    (p: any) => !publishedPlatforms.includes(p)
                  ).length
                } remaining platforms.`}
          </p>
        )}

        {results && (
          <div className="mt-8">
            <h3 className="font-semibold mb-4 text-slate-900">
              {t("publishing_results")}
            </h3>

            {results._summary && (
              <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">
                      {results._summary.total}
                    </div>
                    <div className="text-sm text-gray-500 font-medium">
                      Total
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">
                      {results._summary.successful}
                    </div>
                    <div className="text-sm text-gray-500 font-medium">
                      Successful
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-600">
                      {results._summary.failed}
                    </div>
                    <div className="text-sm text-gray-500 font-medium">
                      Failed
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {Object.entries(results)
                .filter(([key]) => key !== "_summary")
                .map(([platform, result]: [string, any]) => (
                  <div
                    key={platform}
                    className={`border rounded-md p-4 ${
                      result.success
                        ? "bg-green-50 border-green-200"
                        : "bg-red-50 border-red-200"
                    }`}
                  >
                    <h4
                      className={`font-medium ${result.success ? "text-green-800" : "text-red-800"}`}
                    >
                      {result.success ? "✅" : "❌"} {platform}
                    </h4>
                    <p
                      className={`text-sm mt-1 ${result.success ? "text-green-600" : "text-red-600"}`}
                    >
                      {result.message || result.error}
                    </p>
                    {!result.success && result.retryable && (
                      <p className="text-xs text-amber-600 mt-1">
                        💡 Temporary error — try again later
                      </p>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};